import { HelpRounded, MapOutlined } from "@mui/icons-material";
import AccountIcon from "@mui/icons-material/AccountCircle";
import ComputerIcon from "@mui/icons-material/Computer";
import SupportIcon from "@mui/icons-material/ContactSupport";
import SubscriptionIcon from "@mui/icons-material/CreditCard";
import GavelIcon from "@mui/icons-material/Gavel";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import UsersIcon from "@mui/icons-material/PeopleAlt";
import RegisterIcon from "@mui/icons-material/PersonAddAltRounded";
import PolicyIcon from "@mui/icons-material/Policy";
import OptionsIcon from "@mui/icons-material/Style";
import {
	Box,
	BoxProps,
	Divider,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
	useTheme,
} from "@mui/material";
import SwipeableDrawer, { DrawerProps } from "@mui/material/Drawer";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { Link, NavLink } from "react-router-dom";
import SmartOceanLogo from "../assets/img/SmartOcean-White.png";
import BuoyIcon from "../assets/svg/BuoyIcon";
import { useApi } from "../contexts/ApiContext";
import useAuth from "../contexts/AuthContext";
import useUserContext from "../contexts/UserContext";
import AppStoreBadges from "./AppStoreBadges";
import LanguagePicker from "./LanguagePicker";

const menuItemColor = "rgba(255, 255, 255, 0.9)";
const itemStyles = {
	py: 1,
	px: 3,
	color: menuItemColor,
	"&:hover, &:focus, &.active": {
		bgcolor: "rgba(255, 255, 255, 0.08)",
	},
	width: "100%",
};

const categoryStyles = {
	boxShadow: "0 -1px 0 rgb(255,255,255,0.1) inset",
	py: 1.5,
	px: 3,
};

type NavigationItem = {
	id: string;
	icon: ReactNode;
	path?: string;
	onClick?: () => void;
	external?: boolean;
};

function Category({ name, children }: { name: string } & BoxProps) {
	const theme = useTheme();
	return (
		<Box sx={{ bgcolor: theme.palette.primary.dark }}>
			<ListItem sx={{ py: 2, px: 3 }}>
				<ListItemText sx={{ color: "#fff" }}>{name}</ListItemText>
			</ListItem>
			{children}
			<Divider sx={{ mt: 2 }} />
		</Box>
	);
}

function NavLinkMenuItem({ icon, id, path, onClose, onClick, external }: NavigationItem & NavigatorProps) {
	const linkProps = external ? { href: path, target: "_blank", rel: "noopener noreferrer" } : { to: path ?? "" };
	const LinkComponent = external ? "a" : NavLink;
	const isActive = path && window.location.pathname.endsWith(path);

	const listItemButtonProps = {
		sx: itemStyles,
		component: path ? LinkComponent : "button",
		className: isActive ? "active" : "",
		onClick: () => {
			onClick?.();
			onClose?.();
		},
		...(!path && { type: "button" }),
		...linkProps,
	};

	// check if the link is active by comparing the current path with the path of the link, but only the end of the path

	return (
		<ListItemButton {...listItemButtonProps}>
			<ListItemIcon>{icon}</ListItemIcon>
			<ListItemText>{id}</ListItemText>
		</ListItemButton>
	);
}

function AuthenticatedMenuItems({ onClose }: Pick<NavigatorProps, "onClose">) {
	const { t } = useTranslation();
	const { isAccountOwner } = useUserContext();
	const api = useApi();
	const { mutate: navigateToMap } = useMutation(async function navigateToWebOrMobileApp() {
		let visibleChangeEventListener: unknown;

		// async function navigateToWebApp() {
		// 	const token = await api.auth.getTokenForSeamlessLogin();
		// 	const url = import.meta.env.REACT_APP_MAP_URL + "/seamless-login?token=" + token + "&redirect=/map";

		// 	document.removeEventListener("visibilitychange", visibleChangeEventListener as EventListener);
		// 	const newTab = window.open(url, "_blank");
		// 	if (newTab === null) {
		// 		// Unable to open new tab, navigate to URL directly
		// 		window.location.href = url;
		// 	}
		// }

		const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
		if (isMobile) {
			// Attempt to open in the app
			window.location.href = "digicatch://";
			let timeout: number;

			function handleVisibilityChange() {
				if (document.visibilityState === "visible") {
					clearTimeout(timeout);
					document.removeEventListener("visibilitychange", handleVisibilityChange);
					// navigateToWebApp();
				}
			}

			timeout = setTimeout(() => {
				document.removeEventListener("visibilitychange", handleVisibilityChange);
				//navigateToWebApp();
			}, 2000);

			visibleChangeEventListener = document.addEventListener("visibilitychange", handleVisibilityChange);
		} else {
			//navigateToWebApp();
		}
	});

	return (
		<>
			<NavLinkMenuItem
				id={t("menu.account.buoys")}
				icon={<BuoyIcon color={menuItemColor} />}
				path="/account/buoys"
				onClose={onClose}
			/>
			{/* <NavLinkMenuItem
				id={t("menu.account.map")}
				icon={<MapOutlined />}
				onClick={() => navigateToMap()}
				onClose={onClose}
			/> */}
			{isAccountOwner && (
				<NavLinkMenuItem
					id={t("menu.account.subscription")}
					icon={<SubscriptionIcon />}
					path="/account/subscription"
					onClose={onClose}
				/>
			)}
			<NavLinkMenuItem
				id={t("menu.account.chartplotters")}
				icon={<ComputerIcon />}
				path="/account/integrations"
				onClose={onClose}
			/>
			{isAccountOwner && (
				<NavLinkMenuItem
					id={t("menu.account.users")}
					icon={<UsersIcon />}
					path="/account/users"
					onClose={onClose}
				/>
			)}
			<NavLinkMenuItem
				id={t("menu.account.profile")}
				icon={<AccountIcon />}
				path="/account/profile"
				onClose={onClose}
			/>
			<NavLinkMenuItem id={t("logout")} icon={<LogoutIcon />} path="/logout" />
		</>
	);
}

export type NavigatorProps = {
	onClose?: () => void;
} & Omit<DrawerProps, "onClose">;

export default function Navigator({ onClose, ...other }: NavigatorProps) {
	const { t } = useTranslation();
	const { isAuthenticated } = useAuth();
	const homeUrl = isAuthenticated ? "/account" : "/";

	return (
		<SwipeableDrawer variant="permanent" onClose={onClose} {...other}>
			<List
				disablePadding
				sx={{
					minHeight: "100%",
					display: "flex",
					flexDirection: "column",
					justifyContent: "flex-start",
				}}
			>
				<ListItem
					sx={{ ...itemStyles, ...categoryStyles, fontSize: 22, color: "#fff" }}
					component={Link}
					to={homeUrl}
				>
					<img src={SmartOceanLogo} alt="Smart Ocean Logo" style={{ height: "30px" }} />
				</ListItem>
				<Category name={t("menu.account.categoryName")}>
					{isAuthenticated ? (
						<AuthenticatedMenuItems onClose={onClose} />
					) : (
						<>
							<NavLinkMenuItem id={t("login")} icon={<LoginIcon />} path="/login" onClose={onClose} />
							<NavLinkMenuItem
								id={t("menu.account.register")}
								icon={<RegisterIcon />}
								path="/register"
								onClose={onClose}
							/>
						</>
					)}
				</Category>
				<Category name={t("menu.general.categoryName")}>
					<NavLinkMenuItem
						id={t("menu.general.help")}
						external
						icon={<HelpRounded />}
						path="https://resources.smartocean.com/help"
						onClose={onClose}
					/>
					<NavLinkMenuItem
						id={t("menu.general.contact")}
						icon={<SupportIcon />}
						path="/contact-us"
						onClose={onClose}
					/>
					<NavLinkMenuItem id={t("menu.general.pricing")} icon={<OptionsIcon />} path="/pricing" />
					<NavLinkMenuItem
						id={t("menu.general.salesConditions")}
						icon={<GavelIcon />}
						path="/sales-conditions"
						onClose={onClose}
					/>
					<NavLinkMenuItem
						id={t("menu.general.privacyPolicy")}
						icon={<PolicyIcon />}
						path="/privacy-policy"
						onClose={onClose}
					/>
				</Category>
				<ListItemButton sx={{ justifyContent: "center", alignItems: "flex-start", cursor: "default" }}>
					<LanguagePicker sx={{ color: menuItemColor }} />
				</ListItemButton>

				<ListItem sx={{ py: 1.5, px: 3, justifySelf: "flex-end", flexDirection: "column" }}>
					<Typography sx={{ color: menuItemColor, mx: "auto" }}>{t("menu.appStore")}</Typography>
					<AppStoreBadges />
				</ListItem>
			</List>
		</SwipeableDrawer>
	);
}
